import { apiUrl, getPayment, getPaymentDetails, usePaymentRequestMessages } from "@api";
import { updatePayment, useMerchant, useUser } from "@auth";
import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  Dropdown,
  DropdownBody,
  DropdownButton,
  DropdownItem,
  GridSection,
  GridSectionAction,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  MessageContentModal,
  Modal,
  Timeline,
  PreviewOverlay,
  ProfilePaymentIcons,
} from "@components";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import { projectionIdentity } from "@areas/calendar/common";
import { cn, formatAmount, formatDateTime } from "@utils";
import { useEffect, useRef, useState } from "react";
import {
  CheckLg,
  ClockHistory,
  Download,
  Envelope,
  EnvelopeOpen,
  Exclamation,
  Eye,
  BoxArrowUpRight,
  FileEarmark,
  FileEarmarkCode,
  FileEarmarkImage,
  FileEarmarkPdf,
  FileEarmarkSpreadsheet,
  FileEarmarkText,
  FileEarmarkWord,
  Link45deg,
  QrCode,
  ThreeDots,
  X,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import {
  PaperAirplaneIcon,
  ReceiptRefundIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { paymentRequestStatusColors } from "../common";
import CancelRequest from "../flows/CancelRequest";
import IssueCardRefund from "../flows/IssueCardRefund";
import IssuePayByBankRefund from "../flows/IssuePayByBankRefund";
import IssueKlarnaRefund from "../flows/IssueKlarnaRefund";
import MarkAsPaid from "../flows/MarkAsPaid";
import RequestAPayment from "../flows/RequestAPayment";
import SendReminder from "../flows/SendReminder";
import LedgerPayment from "./LedgerPayment";
import SalesOrderPayment from "./SalesOrderPayment";

const PaymentRequestDetails = ({
  paymentRequestId,
  onChange,
  onClose,
  compactActions = false,
  onViewPlan,
  elevated = false,
}) => {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showMarkAsPaid, setShowMarkAsPaid] = useState(false);
  const [showFile, setShowFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showCardRefund, setShowCardRefund] = useState(false);
  const [obRefundPayment, setObRefundPayment] = useState(null);
  const [showKlarnaRefund, setShowKlarnaRefund] = useState(false);
  const [showUpdateRequest, setShowUpdateRequest] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);
  const updateRequestCreated = useRef(false);
  const { user } = useUser();
  const { hasPermission } = useMerchant();
  const { profiles: paymentProfiles } = usePaymentProfilesContext();
  const { getTemplateNameById } = usePaymentRequestTemplatesContext();
  const { data: requestMessages } = usePaymentRequestMessages(paymentRequestId);
  const hasArchivedMessage = (requestMessages ?? []).some((m) => m.hasArchive);
  const paymentProfile =
    paymentRequest && !paymentRequest.amountPaid
      ? paymentProfiles?.find((p) => p.id === paymentRequest.paymentProfileId)
      : null;
  const templateName =
    paymentRequest?.messageTemplateName ||
    (paymentRequest?.paymentRequestTemplateId
      ? getTemplateNameById(paymentRequest.paymentRequestTemplateId)
      : null);
  const successfulPayments =
    paymentRequest?.payments?.filter((p) => p.status === "PaymentSucceeded") ??
    [];

  const onMarkAsPaid = () => {
    setShowMarkAsPaid(false);
    update();
  };

  const onCancel = () => {
    setShowCancel(false);
    update();
  };

  const onReminderSent = () => {
    update();
  };

  const getPaymentRequestDetails = async () => {
    const data = await getPaymentDetails(paymentRequestId);
    setPaymentRequest(data);
  };

  const update = async () => {
    await getPaymentRequestDetails();
    onChange();
  };

  let navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      await getPaymentRequestDetails();
    }
    if (paymentRequestId) {
      fetchData();
    }
  }, [paymentRequestId, setPaymentRequest, navigate]);

  const copyLink = () => {
    navigator.clipboard.writeText(
      window.location.origin.replace("portal", "webpay") +
        "/pay/" +
        paymentRequestId,
    );
    setLinkCopied(true);
    setTimeout(function () {
      setLinkCopied(false);
    }, 3000);
  };

  const onMarkPaidComplete = async () => {
    const data = await getPaymentDetails(paymentRequestId);
    setPaymentRequest(data);
    setShowMarkAsPaid(false);
    if (onChange) {
      await onChange();
    }
  };

  const canRefund = () =>
    paymentRequest.amountPaid &&
    paymentRequest.amountRefunded !== paymentRequest.amountPaid &&
    (paymentRequest.paymentMethod === "Pay by Bank" ||
      paymentRequest.paymentMethod.includes("Card") ||
      (successfulPayments.length === 1 &&
        successfulPayments[0]?.type === "Klarna"));

  const startRefund = async () => {
    if (paymentRequest.paymentMethod === "Pay by Bank") {
      const data = await getPayment(
        paymentRequest.successfulPaymentId,
        "paybybank",
      );
      setObRefundPayment(data);
    } else if (paymentRequest.paymentMethod.includes("Card")) {
      setShowCardRefund(true);
    } else if (
      successfulPayments.length === 1 &&
      successfulPayments[0]?.type === "Klarna"
    ) {
      setShowKlarnaRefund(true);
    }
  };

  const formatVia = () => {
    if (paymentRequest.communicationMethod === "Sms") {
      return (
        <span>
          SMS
          <span className="ml-2 text-zinc-500">
            {paymentRequest.sms?.to || "N/A"}
          </span>
        </span>
      );
    }
    if (paymentRequest.communicationMethod === "Email") {
      return (
        <span>
          Email
          <span className="ml-2 text-zinc-500">
            {paymentRequest.email?.to || "N/A"}
          </span>
        </span>
      );
    }
    if (paymentRequest.communicationMethod === "SmsEmail") {
      return (
        <span>
          SMS
          <span className="ml-2 text-zinc-500">
            {paymentRequest.sms?.to || "N/A"}
          </span>
          <br />
          Email
          <span className="ml-2  text-zinc-500">
            {paymentRequest.email?.to || "N/A"}
          </span>
        </span>
      );
    }
    if (paymentRequest.communicationMethod === "Link") {
      return "Link";
    }
    if (paymentRequest.communicationMethod === "QRCode") {
      return "QR Code";
    }
    return paymentRequest.communicationMethod;
  };

  const mapTimeline = () => {
    let activity = [];

    activity.push({
      title: templateName ? `${templateName} sent` : "Payment request sent",
      subTitle: `${formatDateTime(paymentRequest.requestedOn)} - ${paymentRequest.requestedBy}`,
      amount:
        paymentRequest.amountRequested !== null
          ? formatAmount(paymentRequest.amountRequested)
          : null,
      date: paymentRequest.requestedOn,
      nodeContent: { icon: PaperAirplaneIcon },
      onView: hasArchivedMessage
        ? () =>
            setViewMessage({
              paymentRequestId: paymentRequest.id,
              date: paymentRequest.requestedOn,
            })
        : undefined,
    });

    if (paymentRequest.paidOn) {
      const byMarked = paymentRequest.markedAsPaidBy;
      activity.push({
        title: (
          <span style={{ color: "#15803d" }}>
            {byMarked ? "Marked as paid" : "Payment received"}
          </span>
        ),
        subTitle: byMarked
          ? `${formatDateTime(paymentRequest.paidOn)} - ${byMarked}`
          : `${formatDateTime(paymentRequest.paidOn)} - ${paymentRequest.paymentMethod}`,
        amount: (
          <span style={{ color: "#15803d" }}>
            {formatAmount(paymentRequest.amountPaid)}
          </span>
        ),
        date: paymentRequest.paidOn,
        nodeContent: { icon: CheckLg, color: "success" },
      });
    }

    if (paymentRequest.displayStatus === "Expired") {
      activity.push({
        title: "Payment request expired",
        subTitle: formatDateTime(paymentRequest.expiresOn),
        date: paymentRequest.expiresOn,
        nodeContent: { icon: ClockHistory },
      });
    }

    let notes = paymentRequest.notes.map((x) => ({
      title: "Note added",
      subTitle: `${formatDateTime(x.createdOn)} - ${x.createdBy}`,
      content: x.content,
      taggedUsers: x.taggedUsers,
      date: x.createdOn,
      nodeContent: {
        text: x.createdBy
            ?.split(" ")
            .map((n) => n[0])
            .join("") ?? "",
      },
    }));

    activity = activity.concat(notes);

    if (paymentRequest.email?.deliveredOn) {
      activity.push({
        title: "Email delivered",
        subTitle: formatDateTime(paymentRequest.email.deliveredOn),
        date: paymentRequest.email.deliveredOn,
        nodeContent: { icon: Envelope },
      });
    }

    if (paymentRequest.email?.openedOn) {
      activity.push({
        title: "Email opened",
        subTitle: formatDateTime(paymentRequest.email.openedOn),
        date: paymentRequest.email.openedOn,
        nodeContent: { icon: EnvelopeOpen },
      });
    }

    if (paymentRequest.email?.failedOn) {
      activity.push({
        title: "Email delivery failed",
        subTitle: formatDateTime(paymentRequest.email.failedOn),
        date: paymentRequest.email.failedOn,
        nodeContent: { icon: Exclamation, color: "red" },
      });
    }

    if (paymentRequest.sms?.deliveredOn && !paymentRequest.openedOn) {
      activity.push({
        title: "SMS delivered",
        subTitle: formatDateTime(paymentRequest.sms.deliveredOn),
        date: paymentRequest.sms.deliveredOn,
        nodeContent: { icon: Envelope },
      });
    }

    if (paymentRequest.sms && paymentRequest.openedOn) {
      activity.push({
        title: "SMS opened",
        subTitle: formatDateTime(paymentRequest.openedOn),
        date: paymentRequest.openedOn,
        nodeContent: { icon: EnvelopeOpen },
      });
    }

    if (paymentRequest.sms?.failedOn) {
      activity.push({
        title: "SMS delivery failed",
        subTitle: formatDateTime(paymentRequest.sms.failedOn),
        date: paymentRequest.sms.failedOn,
        nodeContent: { icon: Exclamation, color: "red" },
      });
    }

    const getActionText = () => {
      if (paymentRequest.invoicesRequested) {
        if (paymentRequest.invoicesRequested === "All") return "All invoices";
        if (paymentRequest.invoicesRequested === "OverdueAndDueThisMonth")
          return "Overdue and due";
        if (paymentRequest.invoicesRequested === "None")
          return "No invoices requested";
        return "Overdue";
      }
      return ``;
    };

    let reminderEvents = paymentRequest.reminders.map((x) => ({
      title: `Reminder sent`,
      subTitle: `${formatDateTime(x.sentOn)} - ${getActionText()}`,
      amount:
        paymentRequest.amountRequested !== null
          ? formatAmount(paymentRequest.amountRequested)
          : null,
      date: x.sentOn,
      nodeContent: { icon: BellIcon },
    }));

    activity = activity.concat(reminderEvents);

    let refundEvents = paymentRequest.refunds.map((x) => {
      const refundTitles = {
        Succeeded: "Refund issued",
        PaymentSucceeded: "Refund issued",
        Pending: "Refund processing",
        Processing: "Refund processing",
        Initialized: "Refund created and awaiting authorisation",
        Cancelled: "Refund cancelled",
        Expired: "Refund expired",
        Failed: "Refund failed",
        PaymentFailed: "Refund failed",
      };
      const failed = x.status === "Failed" || x.status === "PaymentFailed";
      return {
        title: refundTitles[x.status] ?? `Refund ${x.status.toLowerCase()}`,
        subTitle: `${formatDateTime(x.issuesOn)} - ${x.issuedBy}`,
        amount: formatAmount(x.amount),
        date: x.issuesOn,
        nodeContent: failed
          ? { icon: ReceiptRefundIcon, color: "red" }
          : { icon: ReceiptRefundIcon },
      };
    });

    activity = activity.concat(refundEvents);

    let paymentEvents = paymentRequest.payments
      .filter((x) => x.status === "PaymentFailed")
      .map((x) => ({
        title: "Payment attempt failed",
        subTitle: formatDateTime(x.initialisedOn),
        date: x.initialisedOn,
        nodeContent: { icon: Exclamation, color: "red" },
      }));

    activity = activity.concat(paymentEvents);

    if (paymentRequest.cancelledOn) {
      activity.push({
        title: "Cancelled",
        subTitle: `${formatDateTime(paymentRequest.cancelledOn)} - ${paymentRequest.cancelledBy}`,
        date: paymentRequest.cancelledOn,
        nodeContent: { icon: X, color: "black" },
      });
    }

    return activity;
  };

  const close = () => {
    setPaymentRequest(null);
    onClose();
  };

  const payment = paymentRequest?.payments?.find(
    (x) => x.status === "PaymentSucceeded",
  );

  const PREVIEWABLE_EXTS = new Set(["pdf", "png", "jpg", "jpeg", "bmp", "gif"]);

  const pickIcon = (ext) => {
    if (
      [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "bmp",
        "svg",
        "webp",
        "tif",
        "tiff",
      ].includes(ext)
    )
      return FileEarmarkImage;
    if (ext === "pdf") return FileEarmarkPdf;
    if (ext === "doc" || ext === "docx") return FileEarmarkWord;
    if (ext === "csv") return FileEarmarkSpreadsheet;
    if (["xml", "html", "htm"].includes(ext)) return FileEarmarkCode;
    if (["txt", "rtf"].includes(ext)) return FileEarmarkText;
    return FileEarmark;
  };

  const getFileExt = (name) => name.split(".").pop().toLowerCase();

  const baseFileUrl = () =>
    `${apiUrl()}/api/paymentrequests/${paymentRequest?.id}/files`;

  const previewUrl = (name) => `${baseFileUrl()}/${name}?isPreview=true`;
  const downloadUrl = (name) => `${baseFileUrl()}/${name}`;

  // Reuses the calendar's promise/indigo projection identities so a plan/promise reads the
  // same here as it does in the calendar and Today's tasks. The "view plan" affordance is
  // only offered when a handler is supplied; opened from within PlanDetailModal it brings
  // the plan modal back (the drawer having replaced it rather than stacked on top).
  const renderPlanContext = () => {
    const planContext = paymentRequest.planContext;
    if (!planContext) return null;
    // A promise and a merchant-scheduled send share actionType "PromiseToPay" — scheduleKind
    // discriminates them (see common.jsx's projectionIdentity), so resolve through it, not actionType.
    const identity = projectionIdentity({
      projection: { actionType: planContext.actionType, scheduleKind: planContext.scheduleKind },
    });
    if (!identity) return null;

    const { Icon } = identity;
    const label =
      planContext.actionType === "PaymentPlan"
        ? `Payment plan · Instalment ${planContext.instalmentNumber} of ${planContext.instalmentCount}`
        : identity.label;
    const viewLabel = "View details";
    const canView = !!onViewPlan;

    const content = (
      <>
        <Icon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
        {canView && (
          <span className="shrink-0 text-sm font-semibold group-hover:underline">
            {viewLabel}
          </span>
        )}
      </>
    );

    if (!canView) {
      return (
        <div className={cn("flex items-center gap-2 border-b border-zinc-200 px-5 py-3", identity.panel)}>
          {content}
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => {
          onViewPlan(planContext.actionId);
          close();
        }}
        className={cn(
          "group flex w-full items-center gap-2 border-b border-zinc-200 px-5 py-3 text-left transition-opacity hover:opacity-90",
          identity.panel,
        )}
      >
        {content}
      </button>
    );
  };

  return (
    <>
    <Drawer
      title="Payment request details"
      isOpen={!!paymentRequest}
      toggle={close}
      elevated={elevated}
    >
      {paymentRequest && (
        <>
          <DrawerBody>
            {renderPlanContext()}
            <GridSection>
              <GridSectionSpan>
                <div>
                  {paymentRequest.invoicesRequested &&
                    paymentRequest.amountRequested !== null && (
                      <p className="text-grey-500 text-sm pb-2">
                        {paymentRequest.invoicesRequested + " invoices"}
                      </p>
                    )}
                  <h2 className="text-3xl font-medium m-0 mr-2 items-center flex gap-3">
                    {paymentRequest.amountRequested !== null ||
                    paymentRequest.amountPaid !== null
                      ? formatAmount(
                          paymentRequest.amountPaid ||
                            paymentRequest.amountRequested,
                        )
                      : paymentRequest.invoicesRequested + " invoices"}
                    <Badge
                      className="mb-4"
                      color={
                        paymentRequestStatusColors[paymentRequest.displayStatus]
                      }
                    >
                      {paymentRequest.displayStatus}
                    </Badge>
                  </h2>
                </div>
              </GridSectionSpan>
              <GridSectionItem>
                <GridSectionHeader>Customer</GridSectionHeader>
                <GridSectionBody>{paymentRequest.customerName}</GridSectionBody>
              </GridSectionItem>
              <GridSectionItem>
                <GridSectionHeader>Payment reference</GridSectionHeader>
                <GridSectionBody>{paymentRequest.reference}</GridSectionBody>
              </GridSectionItem>
            </GridSection>

            <GridSection>
              <GridSectionItem>
                <GridSectionHeader>Source</GridSectionHeader>
                <GridSectionBody>Payment request</GridSectionBody>
              </GridSectionItem>
              <GridSectionItem>
                <GridSectionHeader>Channel</GridSectionHeader>
                <GridSectionBody>{formatVia()}</GridSectionBody>
              </GridSectionItem>
              <GridSectionItem>
                <GridSectionHeader>Requested by</GridSectionHeader>
                <GridSectionBody>{paymentRequest.requestedBy}</GridSectionBody>
              </GridSectionItem>
              <GridSectionItem>
                <GridSectionHeader>Date sent</GridSectionHeader>
                <GridSectionBody>
                  {formatDateTime(paymentRequest.requestedOn)}
                </GridSectionBody>
              </GridSectionItem>
              {paymentProfile && (
                <GridSectionItem>
                  <GridSectionHeader>Payment profile</GridSectionHeader>
                  <GridSectionBody>
                    <span className="flex items-center gap-2">
                      {paymentProfile.name}
                      <ProfilePaymentIcons profile={paymentProfile} size="sm" />
                    </span>
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {templateName && (
                <GridSectionItem>
                  <GridSectionHeader>Template</GridSectionHeader>
                  <GridSectionBody>{templateName}</GridSectionBody>
                </GridSectionItem>
              )}
              {!paymentRequest.paidOn &&
                (paymentRequest.communicationMethod === "QRCode" ? (
                  <GridSectionAction
                    color="secondary"
                    onClick={() => setShowQR(true)}
                  >
                    <QrCode className="mr-2" />
                    Show QR code
                    <Modal
                      isOpen={showQR}
                      onClose={() => setShowQR(false)}
                      title="Payment request QR code"
                    >
                      <img
                        src={`${apiUrl()}/api/paymentrequests/${paymentRequestId}/qrcode`}
                        alt="Payment QR code"
                        className="mx-auto mt-10"
                        style={{ maxWidth: "80%" }}
                      />
                      button
                    </Modal>
                  </GridSectionAction>
                ) : (
                  <GridSectionAction
                    color="secondary"
                    onClick={() => copyLink()}
                  >
                    <Link45deg className="mr-2" />
                    {linkCopied ? "Link copied" : "Copy link"}
                  </GridSectionAction>
                ))}
            </GridSection>
            {paymentRequest.amountPaid && (
              <>
                <div className="w-full flex items-center px-5 pt-5">
                  <p className="font-semibold m-0">Payment details</p>
                </div>
                <GridSection
                  className={
                    payment &&
                    (payment.corporateCardSurcharge != null ||
                      (paymentRequest.refunds &&
                        paymentRequest.refunds.length > 0))
                      ? "grid-cols-2"
                      : "grid-cols-3"
                  }
                >
                  <GridSectionItem>
                    <GridSectionHeader>Date</GridSectionHeader>
                    <GridSectionBody>
                      {formatDateTime(paymentRequest.paidOn)}
                    </GridSectionBody>
                  </GridSectionItem>
                  <GridSectionItem>
                    <GridSectionHeader>Method</GridSectionHeader>
                    <GridSectionBody>
                      {payment && payment.corporateCardSurcharge != null
                        ? "Corporate card"
                        : paymentRequest.paymentMethod}
                    </GridSectionBody>
                  </GridSectionItem>
                  <GridSectionItem>
                    <GridSectionHeader>Amount</GridSectionHeader>
                    <GridSectionBody>
                      {formatAmount(paymentRequest.amountPaid)}
                    </GridSectionBody>
                  </GridSectionItem>
                  {payment && payment.corporateCardSurcharge != null && (
                    <GridSectionItem>
                      {/* TODO: Add the percentage fee */}
                      <GridSectionHeader>Card fee</GridSectionHeader>
                      <GridSectionBody>
                        {formatAmount(payment.corporateCardSurcharge)}
                      </GridSectionBody>
                    </GridSectionItem>
                  )}
                  {paymentRequest.refunds &&
                    paymentRequest.refunds.length > 0 && (
                      <>
                        <GridSectionItem>
                          <GridSectionHeader>Refund issued</GridSectionHeader>
                          <GridSectionBody>
                            {formatAmount(
                              paymentRequest.refunds.reduce(
                                (x, y) => x + y.amount,
                                0,
                              ),
                            )}
                          </GridSectionBody>
                        </GridSectionItem>
                      </>
                    )}
                </GridSection>
              </>
            )}
            {paymentRequest.files?.length > 0 && (
              <GridSection>
                <div className="col-span-2">
                  <p className="font-semibold m-0 mb-2">Attachments</p>
                  <PreviewOverlay
                    isOpen={!!filePreview}
                    title={filePreview?.name}
                    onClose={() => setFilePreview(null)}
                    action={
                      filePreview
                        ? {
                            icon: <BoxArrowUpRight className="w-4 h-4" />,
                            label: "Open in new tab",
                            onClick: () =>
                              window.open(
                                filePreview.url,
                                "_blank",
                                "noopener,noreferrer",
                              ),
                          }
                        : undefined
                    }
                  >
                    {filePreview &&
                      (() => {
                        const ext = getFileExt(filePreview.name);

                        if (
                          [
                            "jpg",
                            "jpeg",
                            "png",
                            "gif",
                            "webp",
                            "bmp",
                            "svg",
                          ].includes(ext)
                        )
                          return (
                            <img
                              src={filePreview.url}
                              className="max-w-full max-h-full object-contain"
                              alt={filePreview.name}
                            />
                          );

                        return (
                          <iframe
                            src={filePreview.url}
                            title={ext === "pdf" ? "PDF preview" : "file"}
                            style={{ width: "100%", height: "100%", border: 0 }}
                          />
                        );
                      })()}
                  </PreviewOverlay>
                  {paymentRequest.files.map((name) => {
                    const ext = getFileExt(name);
                    const canPreview = PREVIEWABLE_EXTS.has(ext);
                    const Icon = pickIcon(ext);

                    return (
                      <div
                        key={name}
                        className="flex items-center mb-2 text-sm"
                      >
                        <Icon className="text-zinc-700 w-4 h-4 mr-2" />
                        <span>{name}</span>
                        <div className="flex items-center ml-auto">
                          {canPreview && (
                            <button
                              className="px-2 py-2 cursor-pointer"
                              onClick={() =>
                                setFilePreview({ name, url: previewUrl(name) })
                              }
                            >
                              {/*<span className="text-primary-500 font-bold">preview</span>*/}
                              <Eye className="text-primary-500 w-4 h-4 mr-2" />
                            </button>
                          )}
                          <a
                            href={downloadUrl(name)}
                            download={name}
                            className="px-2 py-2 cursor-pointer text-primary-500 font-bold flex"
                          >
                            <Download className="text-primary-500 font-bold w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GridSection>
            )}
            <LedgerPayment
              payment={
                paymentRequest.ledgerPayment
                  ? {
                      ...paymentRequest.ledgerPayment,
                      invoicesRequested: paymentRequest.invoicesRequested,
                    }
                  : null
              }
            />
            <SalesOrderPayment salesOrders={paymentRequest.salesOrders} />
            <Timeline
              activity={mapTimeline()}
              paymentRequestId={paymentRequestId}
              onNoteAdded={() => update()}
            />
          </DrawerBody>
          {(paymentRequest.status === "Requested" ||
            paymentRequest.status === "PaymentFailed") && (
            <DrawerFooter>
              {!compactActions && (
                <Button
                  className="mr-auto"
                  color="dangerlink"
                  onClick={() => setShowCancel(true)}
                >
                  Cancel request
                </Button>
              )}
              {!compactActions && (
                <Button
                  color="secondary"
                  onClick={() => setShowMarkAsPaid(true)}
                >
                  <CheckLg
                    aria-hidden="true"
                    className="shrink-0 inline mr-2"
                  />
                  Mark as paid
                </Button>
              )}
              {!compactActions && (
                <Button
                  color="primary"
                  onClick={() => setShowReminderForm(true)}
                >
                  Send reminder
                </Button>
              )}

              {compactActions ? (
                <div className="ml-auto flex items-center gap-x-2">
                  <Dropdown>
                    <DropdownButton>
                      <Button color="secondary" className="h-8">
                        <ThreeDots />
                      </Button>
                    </DropdownButton>
                    <DropdownBody position="left">
                      {!paymentRequest.paymentRequestTemplateId &&
                        !paymentRequest.messageTemplateName && (
                          <DropdownItem
                            closeOnComplete
                            onClick={() => setShowUpdateRequest(true)}
                          >
                            Update request
                          </DropdownItem>
                        )}
                      <DropdownItem
                        closeOnComplete
                        onClick={() => setShowReminderForm(true)}
                      >
                        Send reminder
                      </DropdownItem>
                      <DropdownItem
                        closeOnComplete
                        onClick={() => setShowCancel(true)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Cancel request
                      </DropdownItem>
                    </DropdownBody>
                  </Dropdown>
                  <Button
                    color="primary"
                    onClick={() => setShowMarkAsPaid(true)}
                  >
                    <CheckLg
                      aria-hidden="true"
                      className="shrink-0 inline mr-2"
                    />
                    Mark as paid
                  </Button>
                </div>
              ) : null}
              <RequestAPayment
                isOpen={showUpdateRequest}
                onClose={() => {
                  setShowUpdateRequest(false);
                  if (updateRequestCreated.current) {
                    updateRequestCreated.current = false;
                    close();
                  }
                }}
                onCreated={() => {
                  updateRequestCreated.current = true;
                  onChange?.();
                }}
                updateRequestData={paymentRequest}
              />
              <MarkAsPaid
                isOpen={showMarkAsPaid}
                onClose={() => setShowMarkAsPaid(false)}
                onComplete={() => onMarkAsPaid()}
                paymentRequestId={paymentRequestId}
                amountRequested={paymentRequest.amountRequested}
              />
              <CancelRequest
                isOpen={showCancel}
                onClose={() => setShowCancel(false)}
                onComplete={() => onCancel()}
                paymentRequestId={paymentRequestId}
                amountRequested={paymentRequest.amountRequested}
                customerName={paymentRequest.customerName}
              />
              <SendReminder
                isOpen={showReminderForm}
                onClose={() => setShowReminderForm(false)}
                onComplete={() => onReminderSent()}
                paymentRequest={paymentRequest}
              />
            </DrawerFooter>
          )}
          {canRefund() && (
            <DrawerFooter>
              {hasPermission(updatePayment) && (
                <Button
                  className="ml-auto"
                  color="secondary"
                  onClick={() => startRefund()}
                >
                  Issue a refund
                </Button>
              )}
            </DrawerFooter>
          )}
          <IssueCardRefund
            isOpen={showCardRefund}
            onClose={() => setShowCardRefund(false)}
            onComplete={() => update()}
            amountPaid={paymentRequest.amountPaid}
            paymentId={paymentRequest.successfulPaymentId}
            amountRefunded={paymentRequest.amountRefunded}
            customerName={paymentRequest.customerName}
            isRefundAllowed={paymentRequest.isRefundAllowed}
          />
          <IssueKlarnaRefund
            isOpen={showKlarnaRefund}
            onClose={() => setShowKlarnaRefund(false)}
            onComplete={() => update()}
            amountPaid={paymentRequest.amountPaid}
            paymentId={paymentRequest.successfulPaymentId}
            amountRefunded={paymentRequest.amountRefunded}
            customerName={paymentRequest.customerName}
            isRefundAllowed={paymentRequest.isRefundAllowed}
          />
          <IssuePayByBankRefund
            isOpen={!!obRefundPayment}
            onClose={() => setObRefundPayment(null)}
            onSaved={() => update()}
            payment={obRefundPayment}
          />
        </>
      )}
    </Drawer>

    <MessageContentModal
      isOpen={!!viewMessage}
      onClose={() => setViewMessage(null)}
      paymentRequestId={viewMessage?.paymentRequestId}
      targetDate={viewMessage?.date}
    />
    </>
  );
};

export default PaymentRequestDetails;
